package com.dotdrives.web.pages.updates

import com.dotdrives.controller.ShareAudienceMode
import com.dotdrives.controller.UpdatesController
import com.dotdrives.controller.UpdatesUiState
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotSkeleton
import react.FC
import react.Key
import react.Props
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType

external interface ShareDialogProps : Props {
    var state: UpdatesUiState
    var controller: UpdatesController
    var onClose: () -> Unit
}

/**
 * Staff-only by construction: [UpdatesPage] only ever mounts this dialog from a "Share with
 * givers" button that [ThreadPanel] itself only renders when `isStaff` is true, and
 * `UpdatesController.startShare`/`confirmShare` re-check `isStaff` before issuing any
 * `report_recipients`/audience-preview call either way -- matching `report_recipients_insert`'s
 * `has_perm(org_id,'report.write')` policy, which admits no `field` role at all.
 */
val ShareDialog = FC<ShareDialogProps> { props ->
    val state = props.state
    val report = state.selectedReport
    val selectedCount = state.shareSelectedGiverIds.size

    div {
        className = ClassName("updates-dialog-backdrop")
        role = "presentation".unsafeCast<AriaRole>()
        onClick = { props.onClose() }

        div {
            className = ClassName("updates-dialog")
            role = "dialog".unsafeCast<AriaRole>()
            ariaModal = true
            ariaLabel = "Share update with givers"
            onClick = { it.stopPropagation() }

            div {
                className = ClassName("updates-dialog-head")
                div { className = ClassName("updates-dialog-title"); +"Share \"${report?.title ?: ""}\" 📣" }
                button {
                    type = ButtonType.buttonType
                    className = ClassName("updates-dialog-close")
                    ariaLabel = "Close"
                    onClick = { props.onClose() }
                    +"✕"
                }
            }

            state.shareError?.let { error -> DotBanner { message = error; className = "updates-inline-banner" } }

            if (state.shareLoading) {
                repeat(3) { DotSkeleton { height = "56px" } }
            } else {
                div {
                    className = ClassName("updates-share-recipients")
                    val targetAudience = state.shareTargetAudience
                    if (report?.targetName != null && targetAudience != null) {
                        AudiencePresetRow {
                            icon = "💧"
                            title = "All ${report.targetName} givers"
                            subtitle = if (targetAudience.monthlyPartnerCount > 0) {
                                "${targetAudience.giverIds.size} givers · includes ${targetAudience.monthlyPartnerCount} monthly partners"
                            } else {
                                "${targetAudience.giverIds.size} givers"
                            }
                            selected = state.shareMode == ShareAudienceMode.Target
                            onClick = { props.controller.setShareAudience(ShareAudienceMode.Target) }
                        }
                    }
                    val activeDrivesAudience = state.shareActiveDrivesAudience
                    if (activeDrivesAudience != null) {
                        AudiencePresetRow {
                            icon = "🎯"
                            title = "Only givers on active drives"
                            subtitle = "${activeDrivesAudience.giverIds.size} givers"
                            selected = state.shareMode == ShareAudienceMode.ActiveDrives
                            onClick = { props.controller.setShareAudience(ShareAudienceMode.ActiveDrives) }
                        }
                    }
                    AudiencePresetRow {
                        icon = "✍️"
                        title = "Pick givers manually"
                        subtitle = null
                        selected = state.shareMode == ShareAudienceMode.Manual
                        onClick = { props.controller.setShareAudience(ShareAudienceMode.Manual) }
                    }
                }

                if (state.shareMode == ShareAudienceMode.Manual) {
                    button {
                        type = ButtonType.buttonType
                        className = ClassName("updates-share-select-all")
                        onClick = { props.controller.selectAllShareGivers() }
                        +"Select all"
                    }
                    div {
                        className = ClassName("updates-share-recipients")
                        if (state.shareGivers.isEmpty()) {
                            div { +"This org has no givers to share with yet." }
                        } else {
                            state.shareGivers.forEach { giver ->
                                val selected = giver.id in state.shareSelectedGiverIds
                                button {
                                    key = giver.id.unsafeCast<Key>()
                                    type = ButtonType.buttonType
                                    className = ClassName(
                                        "updates-share-row" + if (selected) " updates-share-row-selected" else "",
                                    )
                                    onClick = { props.controller.toggleShareGiver(giver.id) }
                                    span { className = ClassName("updates-share-row-copy"); +giver.displayName }
                                    if (selected) span { className = ClassName("updates-share-check"); +"✓" }
                                }
                            }
                        }
                    }
                }
            }

            div {
                className = ClassName("updates-dialog-actions")
                button {
                    type = ButtonType.buttonType
                    className = ClassName("dot-button dot-button-secondary")
                    onClick = { props.onClose() }
                    +"Cancel"
                }
                button {
                    type = ButtonType.buttonType
                    className = ClassName("dot-button dot-button-primary")
                    disabled = selectedCount == 0 || state.sharing
                    onClick = { props.controller.confirmShare() }
                    +"Queue email for $selectedCount giver${if (selectedCount == 1) "" else "s"} 💛"
                }
            }
        }
    }
}

private external interface AudiencePresetRowProps : Props {
    var icon: String
    var title: String
    var subtitle: String?
    var selected: Boolean
    var onClick: () -> Unit
}

private val AudiencePresetRow = FC<AudiencePresetRowProps> { props ->
    button {
        type = ButtonType.buttonType
        className = ClassName(
            "updates-share-row" + if (props.selected) " updates-share-row-selected" else "",
        )
        onClick = { props.onClick() }
        span { +props.icon }
        div {
            className = ClassName("updates-share-row-copy")
            div { +props.title }
            props.subtitle?.let { subtitle ->
                span { className = ClassName("updates-row-files"); +subtitle }
            }
        }
        if (props.selected) span { className = ClassName("updates-share-check"); +"✓" }
    }
}
